import { httpRequest } from "../../http";
import {EngagementSerializer} from "@models/serializers/engagementSerializer.ts";
import {Engagement} from "@models/engagement.model.ts";

export interface EngagementListQuery {
    managerId?: number | null;
    /** Employee ids holding Ops Lead. Supersedes the single `managerId` (MPN-1720). */
    managerIds?: number[];
    /** Employee ids holding Client Success Manager. */
    csmIds?: number[];
    /** "Unassigned" in the Ops Lead / CSM checklists: no live role row of that kind. */
    managerUnassigned?: boolean;
    csmUnassigned?: boolean;
    /** Ops Lead OR CSM is the signed-in user. */
    mine?: boolean;
    /**
     * Derived Live / Not Live, not a stored field.
     *
     * Tri-state on purpose. `undefined` is "do not filter", which is not the same as
     * `false`: the checklist offers both options, and ticking both has to mean the same as
     * ticking neither.
     */
    live?: boolean;
    status?: string[];
    type?: string[];
    stage?: string[];
    search?: string;
    /** One of the API's whitelisted sort keys, "-" prefixed to descend. */
    sort?: string;
    /**
     * Show archived engagements INSTEAD of live ones (MPN-1533).
     *
     * Not an inclusive flag. The server returns one set or the other, so the count under
     * the filter is a count of what you are looking at.
     */
    archived?: boolean;
}

export class CommandGetEngagementsList {
    async run(
        providerId: number,
        limit: number,
        offset: number,
        query: EngagementListQuery = {},
    ) {
        const base = `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/engagements/`;
        const params = new URLSearchParams(
            {
                service_provider: String(providerId),
                limit: String(limit),
                offset: String(offset)
            });
        if (query.managerId != null) {
            params.append("manager_id", String(query.managerId));
        }
        for (const id of query.managerIds ?? []) params.append("manager_id", String(id));
        for (const id of query.csmIds ?? []) params.append("csm_id", String(id));
        if (query.managerUnassigned) params.append("manager_unassigned", "true");
        if (query.csmUnassigned) params.append("csm_unassigned", "true");
        if (query.mine) params.append("mine", "true");
        // Only when the filter is actually in use -- see the tri-state note on the type.
        if (query.live !== undefined) params.append("live", String(query.live));
        for (const s of query.status ?? []) params.append("status", s);
        for (const t of query.type ?? []) params.append("type", t);
        for (const s of query.stage ?? []) params.append("stage", s);
        if (query.search && query.search.trim()) {
            params.append("search", query.search.trim());
        }
        if (query.sort) {
            params.append("sort", query.sort);
        }
        // Only sent when true: the server already defaults to hiding archived, and an
        // explicit archived=false on every request would move that decision to the client,
        // where it can drift from what the server actually does.
        if (query.archived) {
            params.append("archived", "true");
        }

        const url = `${base}?${params.toString()}`;
        try {
            const response = await httpRequest<any>(url, { method: "GET", cache: "no-store" });

            return this.deserialize(response);

        } catch (error) {
            console.error('Error fetching data:', error);
            throw error;
        }
    }

    deserialize(data: any) : Engagement[]{
        const serializer = new EngagementSerializer();
        return serializer.deserializeList(data);
    }
}
